// Core imports
import React, { Fragment } from 'react';

// Component imports
import { Card, Col, Row, Typography } from 'antd';
import { SafetyCertificateOutlined, EyeInvisibleOutlined, LockOutlined, ApiOutlined, MailOutlined } from '@ant-design/icons';
import SEO from '../components/SEO/SEO';

// Constant declarations
const { Title, Paragraph } = Typography;

const iconCircleStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 56,
  height: 56,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, rgba(145, 149, 252, 0.15), rgba(191, 193, 255, 0.15))',
  marginBottom: 16
};

const sections = [
  {
    icon: <LockOutlined className="text-primary" style={{ fontSize: 26 }} />,
    title: 'No Personal Information Without Consent',
    content: 'No personal information about users is stored or collected without their explicit consent. If a user decides to share personal information, such as making their email public, they must provide explicit consent to do so.'
  },
  {
    icon: <EyeInvisibleOutlined className="text-primary" style={{ fontSize: 26 }} />,
    title: 'Anonymity and Confidentiality',
    content: [
      'Anonymity is a crucial aspect of the platform, and all necessary measures are taken to protect the identities of users. No personally identifiable information is disclosed unless the user has explicitly consented to it.',
      'Users can explore and submit NDEs without revealing their identity. Users who wish to remain anonymous are encouraged to use a pseudonym or refrain from providing any personally identifiable information.'
    ]
  },
  {
    icon: <ApiOutlined className="text-primary" style={{ fontSize: 26 }} />,
    title: 'Cookies and Similar Technologies',
    content: 'This website only uses essential cookies to enhance your experience. There are no cookies containing behavior or user information.'
  },
  {
    icon: <MailOutlined className="text-primary" style={{ fontSize: 26 }} />,
    title: 'Contact Us',
    content: 'If you have any questions, concerns, or suggestions about this Privacy Policy or privacy practices, please feel free to get in touch. All privacy-related inquiries will be addressed in a timely and transparent manner.'
  }
];

const PrivacyPolicyScreen = () => {
  return (
    <Fragment>
      <SEO
        title="Privacy Policy"
        description="NDE Archive privacy policy. Learn how we protect your anonymity and handle data. No personal information is collected without explicit consent."
        path="/privacy"
      />
      <Row justify="center" style={{ marginTop: 16, marginBottom: 40 }}>
        <Col xs={24} md={20} lg={16}>
          <div className="text-center mb-42">
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 72,
              height: 72,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(145, 149, 252, 0.15), rgba(191, 193, 255, 0.15))',
              marginBottom: 20
            }}>
              <SafetyCertificateOutlined className="text-primary" style={{ fontSize: 36 }} />
            </div>
            <Title level={1} style={{ color: '#1a1a2e', fontWeight: 800, letterSpacing: '-0.5px', marginBottom: 8 }}>Privacy Policy</Title>
            <Paragraph style={{ color: '#555', fontSize: 16, maxWidth: 600, margin: '0 auto', lineHeight: '26px' }}>
              At NDE Archive, your privacy and anonymity are valued. The platform is built on the foundation of respecting the privacy of users and ensuring that personal information remains confidential.
            </Paragraph>
          </div>

          <Row gutter={[24, 24]}>
            {sections.map((section, index) => (
              <Col xs={24} md={12} key={index}>
                <Card
                  className="h-100 elevation hover-elevation-2"
                  bordered={false}
                  style={{ borderRadius: 16, overflow: 'hidden' }}
                >
                  <div style={iconCircleStyle}>{section.icon}</div>
                  <Title level={4} style={{ marginBottom: 12 }}>{section.title}</Title>
                  {Array.isArray(section.content)
                    ? section.content.map((text, i) => (
                      <Paragraph key={i} style={{ color: '#555', fontSize: 15, lineHeight: '24px', marginBottom: i < section.content.length - 1 ? 12 : 0 }}>
                        {text}
                      </Paragraph>
                    ))
                    : <Paragraph style={{ color: '#555', fontSize: 15, lineHeight: '24px', marginBottom: 0 }}>{section.content}</Paragraph>
                  }
                </Card>
              </Col>
            ))}
          </Row>
        </Col>
      </Row>
    </Fragment>
  );
};

export default PrivacyPolicyScreen;
